export interface ApiError {
  statusCode: number;
  message: string;
  errors?: Array<{
    field?: string;
    message: string;
  }>;
  raw?: unknown;
}

function extractFieldErrors(data: any): ApiError["errors"] | undefined {
  if (!data) return undefined;

  // 1.Array errors (ValidationException OR string array)
  if (Array.isArray(data.errors)) {
    return data.errors.map((e: any) => {
      if (typeof e === "string") {
        return { field: undefined, message: e };
      }

      return {
        field: e.field ?? undefined,
        message: e.message ?? String(e),
      };
    });
  }

  // 2.Error bag (object of arrays)
  if (data.errors && typeof data.errors === "object") {
    return Object.entries(data.errors).flatMap(([field, messages]) =>
      Array.isArray(messages)
        ? messages.map((message) => ({
            field,
            message: String(message),
          }))
        : [],
    );
  }

  // 3.Message-only fallback
  if (typeof data.message === "string") {
    return [{ field: undefined, message: data.message }];
  }

  return undefined;
}

function extractMainMessage(data: any, error: any, errors?: ApiError["errors"]): string {
  // Explicit backend message
  if (typeof data?.message === "string") return data.message;
  if (typeof data?.error === "string") return data.error;
  if (typeof data?.help === "string") return data.help;

  // Single error → treat as message
  if (errors?.length === 1 && errors[0]?.message) {
    return errors[0].message;
  }

  // Fetch / JS error
  if (typeof error?.message === "string") {
    return error.message;
  }

  return "Unexpected server error";
}

export function normalizeFetchError(error: any): ApiError {
  const statusCode = error?.statusCode || error?.status || error?.response?.status || 0;

  const data = error?.data ?? {};

  const errors = extractFieldErrors(data);

  return {
    statusCode,
    message: extractMainMessage(data, error, errors),
    errors,
    raw: error,
  };
}
